/**
 * "Yahoo Contacts" source of the Address Book / Select Recipients modal: read-only contacts from a
 * Yahoo, Ymail, RocketMail or AOL account (Yahoo OAuth 2.0 + CardDAV).
 * Guide: Sendlinx_Yahoo_Contacts_Developer_Guide_Updated.docx §3 and §14.
 *
 * States: Pending approval (server not enabled yet: Yahoo hasn't approved contacts access),
 * Disconnected ("Connect Yahoo Account": Yahoo-hosted consent in a popup, so the Send page keeps
 * typed recipients, message and dropped files), Connected (Last synced, Sync now, Disconnect),
 * Expired (Reconnect) and Sync error (shown from the last sync's error).
 * Rows feed the modal's single shared selection ({ email, name }); Sendlinx never writes to Yahoo.
 * Ymail / RocketMail / AOL addresses are never rejected by domain: everyone signs in through Yahoo.
 */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Sync from "@material-ui/icons/Sync";
import AccountBoxOutlined from "@material-ui/icons/AccountBoxOutlined";
import HourglassEmptyOutlined from "@material-ui/icons/HourglassEmptyOutlined";
import LinkIcon from "@material-ui/icons/Link";
import YahooContactsApi, { openYahooSignInPopup, runYahooSignIn, closeYahooSignInPopup, yahooSignInSameTab,
    YAHOO_RESULT_KEY, YAHOO_MESSAGE } from "../../../../assets/js/utils/contacts/yahooContactsApi";

const PAGE = 50;
const AUTO_SYNC_AFTER_MS = 60 * 60 * 1000;
const lower = (v) => String(v || "").toLowerCase();
const initials = (c) => {
    const src = (c.name || c.email || "?").replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/);
    return ((src[0] || "?")[0] + (src.length > 1 ? src[src.length - 1][0] : "")).toUpperCase();
};
const formatWhen = (iso) => {
    if(!iso){ return ""; }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};
// Signing in to Yahoo is not permission to read contacts (CardDAV access is a separate Yahoo approval).
const ACCESS_DENIED = "You're signed in to Yahoo, but Yahoo hasn't authorized Sendlinx to read your contacts yet. Your Yahoo account stays connected and Sendlinx will try again; use Sync now to check.";
const messageOf = (err, fallback) => (err && err.message && err.message !== "Request Cancelled" ? err.message : fallback);
const SYNC_ERRORS = {
    forbidden: "Yahoo did not allow Sendlinx to read your contacts. Contacts access may not be approved for this account yet; Sendlinx will try again.",
    pending_approval: "Yahoo hasn't approved Sendlinx's contacts access yet.",
    throttled: "Yahoo asked Sendlinx to slow down. The next sync will try again.",
    network: "Yahoo could not be reached during the last sync.",
    too_large: "Your Yahoo address book is too large to import.",
    truncated: "Only the first 25,000 Yahoo contacts were imported."
};

/** Yahoo "Y!" mark drawn as text (no Yahoo logo asset is bundled). */
export function YahooMark({ className }){
    return <span className={className} aria-hidden="true">Y!</span>;
}
YahooMark.propTypes = { className: PropTypes.string };

function YahooContactsView({ classes, isSelected, toggle, toggleMany, already }){
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [busy, setBusy] = useState(false);               // connecting / disconnecting
    const [syncing, setSyncing] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);
    const [confirmDisconnect, setConfirmDisconnect] = useState(false);
    const [notice, setNotice] = useState(null);            // { kind: "ok"|"error"|"info", text }
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState({ key: "name", dir: 1 });
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({ total: 0, page: 0, hasMore: false, loading: false });
    const [listError, setListError] = useState(null);
    const requestId = useRef(0);
    const cancel = useRef(null);
    const mounted = useRef(true);
    const popupRef = useRef(null);
    const cancelled = useRef(false);
    const autoSynced = useRef(false);
    const handledAt = useRef(null);         // a result arrives by postMessage and by storage event; handle it once
    const connected = !!(status && status.connected);

    const applyFailure = (err) => {
        const reason = err && err.reason;
        if(reason === "reconnect" || reason === "not_connected"){
            setStatus(s => ({ ...(s || {}), configured: true, connected: false, status: reason === "reconnect" ? "reconnect" : "disconnected" }));
            setRows(null);
            return true;
        }
        return false;
    };

    const loadStatus = () => {
        setStatusError(null);
        return YahooContactsApi.status().then(data => {
            if(mounted.current){ setStatus(data); }
            return data;
        }).catch(err => {
            if(mounted.current){ setStatusError(messageOf(err, "Your Yahoo account status could not be loaded.")); setStatus(s => s || { configured: true, connected: false, status: "disconnected" }); }
            return null;
        });
    };

    const loadContacts = (page = 1) => {
        const id = ++requestId.current;
        if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        const source = axios.CancelToken.source();
        cancel.current = source;
        setListError(null);
        setMeta(m => ({ ...m, loading: true }));
        return YahooContactsApi.contacts({ q: search.trim(), sort: sort.key, dir: sort.dir === 1 ? "asc" : "desc", page, limit: PAGE }, source).then(data => {
            if(id !== requestId.current || !mounted.current){ return; }
            const items = data.items || [];
            setRows(prev => (page === 1 || !prev ? items : prev.concat(items.filter(r => !prev.some(p => p.id === r.id)))));
            setMeta({ total: data.total || 0, page, hasMore: !!data.hasMore, loading: false });
        }).catch(err => {
            if(id !== requestId.current || !mounted.current || (err && err.message === "Request Cancelled")){ return; }
            setMeta(m => ({ ...m, loading: false }));
            if(!applyFailure(err)){
                setRows(prev => prev || []);
                setListError(messageOf(err, "Your Yahoo contacts could not be loaded."));
            }
        });
    };

    const runSync = (quiet = false) => {
        if(syncing){ return Promise.resolve(); }
        setSyncing(true);
        if(!quiet){ setNotice(null); }
        return YahooContactsApi.sync(false).then(data => {
            if(!mounted.current){ return; }
            setSyncing(false);
            setStatus(data);
            const result = data.result || {};
            if(!quiet){
                setNotice(result.alreadyRunning ? { kind: "info", text: "A sync is already running. The list updates when it finishes." }
                    : { kind: "ok", text: "Yahoo contacts synced." });
            }
            loadContacts(1);
        }).catch(err => {
            if(!mounted.current){ return; }
            setSyncing(false);
            if(applyFailure(err)){ loadStatus(); return; }
            if(!quiet){ setNotice({ kind: "error", text: messageOf(err, "Your Yahoo contacts could not be synced.") }); }
            loadStatus();
        });
    };

    useEffect(() => {
        mounted.current = true;
        loadStatus();
        const onResult = (payload) => {
            if(!payload || !mounted.current || (payload.at && payload.at === handledAt.current)){ return; }
            handledAt.current = payload.at || null;
            // A sign-in started from this view is handled by connect(); this covers other windows.
            if(popupRef.current){ return; }
            if(payload.ok){ setNotice({ kind: "ok", text: "Yahoo account connected." }); setPopupBlocked(false); }
            else{ setNotice({ kind: "error", text: payload.message || "Your Yahoo account was not connected." }); }
            closeYahooSignInPopup(popupRef.current);
            setBusy(false);
            loadStatus();
        };
        const onStorage = (e) => { if(e.key === YAHOO_RESULT_KEY && e.newValue){ try{ onResult(JSON.parse(e.newValue)); }catch(x){ } } };
        const onMessage = (e) => { if(e.origin === window.location.origin && e.data && e.data.type === YAHOO_MESSAGE){ onResult(e.data); } };
        window.addEventListener("storage", onStorage);
        window.addEventListener("message", onMessage);
        try{
            const raw = sessionStorage.getItem(YAHOO_RESULT_KEY);
            if(raw){
                sessionStorage.removeItem(YAHOO_RESULT_KEY);
                const r = JSON.parse(raw);
                setNotice(r.ok ? { kind: "ok", text: "Yahoo account connected." } : { kind: "error", text: r.message || "Your Yahoo account was not connected." });
            }
        }catch(e){ }
        return () => {
            mounted.current = false;
            closeYahooSignInPopup(popupRef.current);
            window.removeEventListener("storage", onStorage);
            window.removeEventListener("message", onMessage);
            if(cancel.current){ cancel.current.cancel("Request Cancelled"); }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!connected){ setRows(null); return; }
        // Background refresh when the last good sync is old (the server also syncs hourly).
        if(!autoSynced.current){
            autoSynced.current = true;
            const last = status && status.lastSyncedAt ? new Date(status.lastSyncedAt).getTime() : 0;
            const running = status && status.sync && status.sync.status === "running";
            if(!running && (!last || Date.now() - last > AUTO_SYNC_AFTER_MS)){ runSync(true); }
        }
    }, [connected]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if(!connected){ return undefined; }
        const timer = setTimeout(() => loadContacts(1), rows === null ? 0 : 300);
        return () => clearTimeout(timer);
    }, [connected, search, sort]); // eslint-disable-line react-hooks/exhaustive-deps

    const connect = () => {
        setNotice(null);
        setPopupBlocked(false);
        const popup = openYahooSignInPopup();
        if(!popup){ setPopupBlocked(true); return; }
        popupRef.current = popup;
        cancelled.current = false;
        setBusy(true);
        runYahooSignIn(popup).then(result => {
            popupRef.current = null;
            if(result && result.at){ handledAt.current = result.at; }
            if(!mounted.current){ return; }
            setBusy(false);
            if(result && result.closed && cancelled.current){
                setNotice({ kind: "info", text: "Yahoo sign-in cancelled. Nothing was connected." });
                loadStatus();
                return;
            }
            if(result && result.timedOut){
                setNotice({ kind: "info", text: "The Yahoo sign-in took too long and was closed. Please connect again." });
                loadStatus();
                return;
            }
            if(result && result.ok){
                const syncError = result.status && result.status.syncError;
                setNotice(syncError ? { kind: "info", text: syncError.reason === "forbidden" ? ACCESS_DENIED
                        : "Yahoo account connected, but the first import didn't finish: "+syncError.message+" Use Sync now to try again." }
                    : { kind: "ok", text: "Yahoo account connected and contacts imported." });
                autoSynced.current = true;
                if(result.status){ setStatus(result.status); } else { loadStatus(); }
            }else if(result && result.closed){
                loadStatus().then(s => {
                    if(mounted.current && !(s && s.connected)){
                        setNotice({ kind: "info", text: "The Yahoo sign-in window was closed before it finished. If you completed it, this view updates automatically." });
                    }
                });
            }else{
                setNotice({ kind: "error", text: (result && result.message) || "Your Yahoo account was not connected." });
                loadStatus();
            }
        }).catch(err => {
            popupRef.current = null;
            if(!mounted.current){ return; }
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Yahoo sign-in could not be started.") });
        });
    };
    const cancelSignIn = () => {
        cancelled.current = true;
        closeYahooSignInPopup(popupRef.current);
    };
    const continueInThisTab = () => {
        setBusy(true);
        yahooSignInSameTab().catch(err => {
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Yahoo sign-in could not be started.") });
        });
    };
    const disconnect = () => {
        setBusy(true);
        YahooContactsApi.disconnect().then(data => {
            if(!mounted.current){ return; }
            setBusy(false);
            setConfirmDisconnect(false);
            setStatus(data);
            setSearch("");
            autoSynced.current = false;
            setNotice({ kind: "ok", text: "Yahoo account disconnected. Sendlinx deleted its access tokens and the imported contacts. Recipients you already selected stay selected." });
        }).catch(err => {
            if(!mounted.current){ return; }
            setBusy(false);
            setNotice({ kind: "error", text: messageOf(err, "Your Yahoo account could not be disconnected.") });
        });
    };

    const sortHeader = (key, label) => (
        <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
            <button type="button" className={classes.sortButton} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}>
                {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
            </button>
        </th>
    );

    const header = (
        <div className={classes.msHeader}>
            <YahooMark className={classes.yhTile} />
            <div>
                <h3 className={classes.msTitle}>Yahoo Contacts</h3>
                <p className={classes.muted}>Yahoo, Ymail, RocketMail &amp; AOL</p>
            </div>
        </div>
    );
    const noticeBox = notice ? (
        <div className={notice.kind === "error" ? classes.error : notice.kind === "ok" ? classes.success : classes.notice} role={notice.kind === "error" ? "alert" : "status"}>{notice.text}</div>
    ) : null;

    if(status === null){
        return <>{header}<p className={classes.empty}>Checking your Yahoo account connection…</p></>;
    }

    if(!connected){
        const reconnect = status.status === "reconnect";
        const pending = status.status === "pending" || status.configured === false;
        return (<>
            {header}
            {noticeBox}
            {statusError ? <div className={classes.error} role="alert">{statusError}</div> : null}
            <div className={classes.yhPanel}>
                <h4 className={classes.yhConnectTitle}>
                    <span className={classes.yhTileSmall} aria-hidden="true"><AccountBoxOutlined /></span>
                    {reconnect ? "Reconnect your Yahoo account" : "Connect your Yahoo account"}
                </h4>
                {reconnect ?
                    <p className={classes.msWarn} role="status"><ErrorOutline aria-hidden="true" />Your Yahoo connection{status.account && status.account.email ? " ("+status.account.email+")" : ""} expired or was revoked. Reconnect to keep using your Yahoo contacts.</p>
                : <p className={classes.msLead}>Access your Yahoo, Ymail, AOL or RocketMail contacts securely.</p>}
                {pending ?
                    <div className={classes.notice} role="status">
                        <HourglassEmptyOutlined aria-hidden="true" style={{ verticalAlign: "-5px", marginRight: 6, fontSize: 20 }} />
                        Pending approval: Yahoo Contacts will be available soon. Sendlinx is waiting for Yahoo to approve contacts access.
                    </div>
                : null}
                <button type="button" className={classes.yhConnectButton} onClick={connect} disabled={busy || pending}>
                    <LinkIcon aria-hidden="true" />
                    {busy ? "Waiting for Yahoo sign-in…" : reconnect ? "Reconnect Yahoo Account" : "Connect Yahoo Account"}
                </button>
                {busy && popupRef.current ?
                    <div className={classes.msWaiting} role="status">
                        <span>Finish signing in in the Yahoo window. After you allow access, Sendlinx imports your contacts; this can take a few seconds.</span>
                        <button type="button" className={classes.secondary} onClick={cancelSignIn}>Cancel sign-in</button>
                    </div>
                : null}
                <ul className={classes.msList}>
                    <li>You will sign in on Yahoo's secure page. Sendlinx never sees your Yahoo password.</li>
                    <li>Sendlinx only requests read-only access to your contacts.</li>
                    <li>Supports Yahoo (yahoo.com, yahoo.ca), Ymail (ymail.com), AOL (aol.com) and RocketMail (rocketmail.com) accounts, subject to Yahoo's approved account compatibility.</li>
                    <li>Sendlinx keeps a copy of your contacts' names and email addresses so you can pick recipients. It is private to your Sendlinx account and deleted when you disconnect.</li>
                </ul>
                {popupBlocked ?
                    <div className={classes.notice} role="alert">
                        Your browser blocked the Yahoo sign-in window. Allow pop-ups for this site and try again, or
                        {" "}<button type="button" className={classes.inlineLink} onClick={continueInThisTab} disabled={busy}>sign in in this tab</button>.
                        {" "}Signing in in this tab reloads the page, so typed recipients, the message and selected files are cleared.
                    </div>
                : null}
            </div>
        </>);
    }

    const list = rows || [];
    const allShown = list.length > 0 && list.every(r => isSelected(r.email));
    const filtered = search.trim() !== "";
    const synced = formatWhen(status.lastSyncedAt);
    const sync = status.sync || {};
    const running = syncing || sync.status === "running";
    const access = status.contactsAccess;       // "unverified" | "authorized" | "denied"
    const syncProblem = access === "denied" ? ACCESS_DENIED
        : sync.status === "error" && sync.error ? (SYNC_ERRORS[sync.error] || "The last sync didn't finish. Use Sync now to try again.")
        : sync.error === "truncated" ? SYNC_ERRORS.truncated
        : access === "unverified" && !running && !sync.status ? "You're signed in to Yahoo. Your contacts appear here after Yahoo confirms Sendlinx's contacts access on the first sync."
        : null;
    return (<>
        {header}
        {noticeBox}
        <div className={classes.msConnected}>
            <CheckCircle className={classes.msOk} aria-hidden="true" />
            <span className={classes.msAccount}>Connected to <strong>{(status.account && (status.account.email || status.account.name)) || "your Yahoo account"}</strong>
                <span className={classes.subLine}>{running ? "Syncing…" : synced ? "Last synced: "+synced : "Not synced yet"}
                    {!running && typeof sync.contactCount === "number" && synced ? " · "+sync.contactCount+" contact"+(sync.contactCount === 1 ? "" : "s") : ""}</span></span>
            <button type="button" className={classes.linkButton} onClick={() => runSync(false)} disabled={running || busy}
                aria-label="Sync Yahoo contacts now"><Sync aria-hidden="true" />{running ? "Syncing…" : "Sync now"}</button>
            {confirmDisconnect ? <span className={classes.msConfirm} role="group" aria-label="Confirm disconnect">
                <span>Disconnect this account?</span>
                <button type="button" className={classes.danger} onClick={disconnect} disabled={busy}>{busy ? "Disconnecting…" : "Disconnect"}</button>
                <button type="button" className={classes.secondary} onClick={() => setConfirmDisconnect(false)} disabled={busy}>Keep</button>
            </span> :
            <button type="button" className={classes.msDisconnect} onClick={() => setConfirmDisconnect(true)}>Disconnect</button>}
        </div>
        {confirmDisconnect ?
            <p className={classes.hint}>Sendlinx deletes its Yahoo access tokens and the imported contacts. Nothing is deleted in your Yahoo account. To remove Sendlinx's access at Yahoo as well, review the connected apps in your Yahoo account's security settings.</p>
        : null}
        {syncProblem ? <div className={classes.notice} role="status">{syncProblem}</div> : null}
        <div className={classes.toolbar}>
            <input type="search" className={classes.search} placeholder="Search contacts by name, email or company…" aria-label="Search Yahoo contacts"
                value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {listError ? <div className={classes.error} role="alert">{listError} <button type="button" className={classes.inlineLink} onClick={() => loadContacts(1)}>Try again</button></div> : null}
        <div className={classes.tableWrap} aria-busy={meta.loading}>
            <table className={classes.table}>
                <thead><tr>
                    <th scope="col" style={{ width: 44 }}>
                        <input type="checkbox" className={classes.checkbox} aria-label={"Select all "+list.length+" Yahoo contacts shown"}
                            checked={allShown} disabled={list.length === 0} onChange={(e) => toggleMany(list, e.target.checked)} />
                    </th>
                    {sortHeader("name", "Name")}{sortHeader("email", "Email")}{sortHeader("company", "Company")}
                </tr></thead>
                <tbody>
                    {rows === null ? <tr><td colSpan={4} className={classes.empty}>Loading your Yahoo contacts…</td></tr> :
                        list.length === 0 ? <tr><td colSpan={4} className={classes.empty}>{filtered ? "No Yahoo contacts match." : running ? "Importing your Yahoo contacts…" : "No contacts with an email address were found in your Yahoo account."}</td></tr> :
                        list.map(r => (
                            <tr key={r.id} className={isSelected(r.email) ? classes.rowSelected : ""}>
                                <td><input type="checkbox" className={classes.checkbox} checked={isSelected(r.email)} onChange={() => toggle(r)}
                                    aria-label={"Select "+(r.name ? r.name+" ("+r.email+")" : r.email)} /></td>
                                <td>
                                    <span className={classes.nameCell}>
                                        <span className={classes.avatar} aria-hidden="true">{initials(r)}</span>
                                        <span>{r.name || <span className={classes.muted}>—</span>}{already.has(lower(r.email)) ? <span className={classes.muted}> (added)</span> : null}</span>
                                    </span>
                                </td>
                                <td>{r.email}</td>
                                <td>{r.company || <span className={classes.muted}>—</span>}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
        {rows !== null && list.length > 0 ?
            <div className={classes.toolbar}>
                <span className={classes.muted}>
                    Showing {list.length} of {meta.total}{filtered ? " matching" : ""}.
                    {allShown && meta.hasMore ? " All shown are selected; load more to select the rest." : " Select all applies to the rows shown."}
                </span>
                {meta.hasMore ?
                    <button type="button" className={classes.linkButton} disabled={meta.loading} onClick={() => loadContacts(meta.page + 1)}>
                        {meta.loading ? "Loading…" : "Load more"}
                    </button>
                : null}
            </div>
        : null}
    </>);
}

YahooContactsView.propTypes = {
    classes: PropTypes.object.isRequired,
    isSelected: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    toggleMany: PropTypes.func.isRequired,
    already: PropTypes.object.isRequired
};

export default YahooContactsView;
